import {
  IconAdd,
  IconChannelSelective,
  IconNFCReaderSmall,
  IconTrash,
} from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import Input from '@/ui/Input/Input';
import CustomSelect from '@/ui/Select/Select';
import { T } from '@/ui/Text/Text';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { createId } from '@paralleldrive/cuid2';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  nfcReaders: GeneralSettingsType['nfcReaders'];
  setNfcReaders: (newValue: GeneralSettingsType['nfcReaders']) => void;
  moreNexmoNFCAllowed: boolean;
  moreTHNFCAllowed: boolean;
}

function NFCReaderHardwareSettings({
  nfcReaders,
  setNfcReaders,
  moreNexmoNFCAllowed,
  moreTHNFCAllowed,
}: Props) {
  const intl = useIntl();
  const toast = useToast();

  const nfcReaderOptions = useMemo(() => {
    if (moreNexmoNFCAllowed && moreTHNFCAllowed) {
      return [
        { value: 'TH-NFC', label: 'TH-NFC' },
        { value: 'Nexmo-NFC', label: 'Nexmo-NFC' },
      ];
    }
    if (!moreNexmoNFCAllowed && moreTHNFCAllowed) {
      return [{ value: 'TH-NFC', label: 'TH-NFC' }];
    }
    if (moreNexmoNFCAllowed && !moreTHNFCAllowed) {
      return [{ value: 'Nexmo-NFC', label: 'Nexmo-NFC' }];
    }
    return [];
  }, [moreNexmoNFCAllowed, moreTHNFCAllowed]);

  // Utility functions
  const updateNfcReader = (
    index: number,
    key: 'type' | 'channel',
    value: string | null,
  ) => {
    const newNfcReaders = [...nfcReaders];
    newNfcReaders[index] = {
      ...newNfcReaders[index],
      [key]: value,
    };
    setNfcReaders(newNfcReaders);
  };

  const deleteNfcReader = (index: number) => {
    const newNfcReaders = nfcReaders.filter((_, i) => i !== index);
    setNfcReaders(newNfcReaders);
  };

  const addNfcReader = () => {
    if (!moreNexmoNFCAllowed && !moreTHNFCAllowed) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'No available NFC Readers',
          id: 'u0Bl/M',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'To add more NFC Readers, please register additional controllers.',
          id: 'F81Xwv',
        }),
        time: DURATIONS.Medium,
      });
      return;
    }
    setNfcReaders([
      ...nfcReaders,
      {
        id: createId(),
        type: null,
        channel: null,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered NFC Readers',
          id: 'dKOKFz',
        })}
      </T>

      {nfcReaders.length !== 0 && (
        <StyledGridContainer columns={2}>
          <>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Type', id: '+U6ozc' })}
            </T>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Channel',
                id: 'KeO51o',
              })}
            </T>
            <div />
          </>

          {nfcReaders.map((nfcReader, index) => {
            const hasAllValues = Boolean(nfcReader.type && nfcReader.channel);
            return (
              <React.Fragment key={nfcReader.id}>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <CustomSelect
                    required
                    options={nfcReaderOptions}
                    value={
                      nfcReader.type
                        ? { value: nfcReader.type, label: nfcReader.type }
                        : null
                    }
                    onChange={(e) => {
                      updateNfcReader(
                        index,
                        'type',
                        e && !Array.isArray(e) ? e.value : null,
                      );
                    }}
                    size="small"
                    isSearchable={false}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Type',
                      id: '+U6ozc',
                    })}
                    icon={<IconNFCReaderSmall />}
                  />
                </div>

                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={nfcReader.channel || ''}
                    onChange={(e) =>
                      updateNfcReader(index, 'channel', String(e))
                    }
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Channel',
                      id: 'KeO51o',
                    })}
                    icon={<IconChannelSelective />}
                  />
                </div>

                <Button
                  size="minimum"
                  mode="transparent"
                  onClick={() => deleteNfcReader(index)}
                >
                  <IconTrash />
                </Button>
              </React.Fragment>
            );
          })}
        </StyledGridContainer>
      )}

      <Button mode="transparent" onClick={addNfcReader}>
        <IconAdd />
      </Button>
    </Flex>
  );
}

export default NFCReaderHardwareSettings;
